import { useMutation } from '@apollo/client/react'
import { useCallback } from 'react'

import type { VariablesOf } from '~publish/graphql'
import { graphql } from '~publish/graphql'

export const SubmitProductFeedback = graphql(/* GraphQL */ `
  mutation SubmitProductFeedback($input: SubmitProductFeedbackInput!) {
    submitProductFeedback(input: $input) {
      __typename
      ... on SubmitProductFeedbackSuccess {
        submittedAt
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

type SubmitProductFeedbackInput = VariablesOf<
  typeof SubmitProductFeedback
>['input']

type UseSubmitProductFeedbackReturn = {
  /** Resolves once the feedback is filed, and throws when it is not. */
  submitProductFeedback: (input: SubmitProductFeedbackInput) => Promise<void>
}

/**
 * The account is read from the session server-side, so no identifying field
 * travels from the browser. The Worker path takes both from the payload, which
 * anyone could set to any account.
 */
export function useSubmitProductFeedback(): UseSubmitProductFeedbackReturn {
  const [mutate] = useMutation(SubmitProductFeedback)

  const submitProductFeedback = useCallback(
    async (input: SubmitProductFeedbackInput): Promise<void> => {
      const { data } = await mutate({ variables: { input } })
      const result = data?.submitProductFeedback

      // A network or GraphQL-level failure already rejected above. This catches
      // the payload's error member, which arrives on a 200.
      if (result?.__typename !== 'SubmitProductFeedbackSuccess') {
        throw new Error(
          result?.__typename === 'VoidMutationError'
            ? result.message
            : 'Feedback was not filed',
        )
      }
    },
    [mutate],
  )

  return { submitProductFeedback }
}
